import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useMarketingCopy } from '../context/MarketingCopyContext';
import { localizedPath } from '../utils/routes';
import SEOHead from '../components/SEOHead';

export default function Login() {
  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { mc } = useMarketingCopy();
  const { lang } = useParams();
  const currentLang = lang || language;
  const lp = (routeName) => localizedPath(routeName, currentLang);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const user = await login(form.email, form.password);
      if (user.role === 'admin') {
        navigate(lp('admin'));
      } else if (!user.email_verified) {
        navigate(lp('verify-email'));
      } else {
        navigate(lp('dashboard'));
      }
    } catch (err) {
      setError(err.response?.data?.error || t.auth.login.invalidCredentials);
    } finally { setLoading(false); }
  };

  return (
    <div style={{ background: '#0C1220', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
      <SEOHead page="login" />
      <div style={{ width: '100%', maxWidth: '440px' }}>
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: '2rem', color: '#D42B2B', marginBottom: '0.3rem' }}>CJPA</div>
          <div style={{ fontSize: '0.58rem', letterSpacing: '0.1em', color: '#64748B', textTransform: 'uppercase' }}>Mécanique Auto</div>
          <h1 style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: '2.2rem', color: '#E8ECF0', fontWeight: 300, marginTop: '2rem' }}>{t.auth.login.title}</h1>
          <p style={{ color: '#94A3B8', fontSize: '0.78rem', marginTop: '0.5rem', letterSpacing: '0.05em' }}>{mc('login', 'subtitle', t.auth.login.subtitle)}</p>
        </div>

        <div style={{ background: '#111827', border: '1px solid rgba(148,163,184,0.15)', padding: 'clamp(1.5rem, 3vw, 2.5rem)' }}>
          {error && <div style={{ background: 'rgba(180,50,50,0.1)', border: '1px solid rgba(180,50,50,0.3)', color: '#e08080', padding: '0.8rem 1rem', fontSize: '0.78rem', marginBottom: '1.5rem', letterSpacing: '0.05em' }}>{error}</div>}

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', fontSize: '0.6rem', letterSpacing: '0.1em', color: '#64748B', textTransform: 'uppercase', marginBottom: '0.6rem', fontFamily: "'Barlow', sans-serif" }}>{t.auth.login.emailLabel}</label>
              <input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})}
                required style={{
                  width: '100%', background: '#1a2332', border: '1px solid rgba(148,163,184,0.2)',
                  color: '#E8ECF0', padding: '0.9rem 1rem', fontSize: '0.85rem',
                  fontFamily: "'Barlow', sans-serif", outline: 'none',
                  transition: 'border-color 0.3s', boxSizing: 'border-box',
                }}
                onFocus={e => e.target.style.borderColor = '#D42B2B'}
                onBlur={e => e.target.style.borderColor = 'rgba(148,163,184,0.2)'}
              />
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', fontSize: '0.6rem', letterSpacing: '0.1em', color: '#64748B', textTransform: 'uppercase', marginBottom: '0.6rem', fontFamily: "'Barlow', sans-serif" }}>{t.auth.login.passwordLabel}</label>
              <input type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})}
                required style={{
                  width: '100%', background: '#1a2332', border: '1px solid rgba(148,163,184,0.2)',
                  color: '#E8ECF0', padding: '0.9rem 1rem', fontSize: '0.85rem',
                  fontFamily: "'Barlow', sans-serif", outline: 'none',
                  transition: 'border-color 0.3s', boxSizing: 'border-box',
                }}
                onFocus={e => e.target.style.borderColor = '#D42B2B'}
                onBlur={e => e.target.style.borderColor = 'rgba(148,163,184,0.2)'}
              />
            </div>

            <div style={{ textAlign: 'right', marginTop: '0.3rem', marginBottom: '0.8rem' }}>
              <Link to={lp('forgot-password')} style={{ color: '#94A3B8', fontSize: '0.72rem', textDecoration: 'none', letterSpacing: '0.03em' }}>{t.auth.login.forgotPassword}</Link>
            </div>

            <button type="submit" disabled={loading} style={{
              width: '100%', background: '#D42B2B', color: '#0C1220', border: 'none',
              padding: '1rem', fontFamily: "'Barlow', sans-serif", fontSize: '0.65rem',
              letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 600,
              cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1,
              marginTop: '0.5rem',
            }}>{loading ? t.auth.login.signing : t.auth.login.button}</button>
          </form>

          <div style={{ textAlign: 'center', marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid rgba(148,163,184,0.1)' }}>
            <span style={{ color: '#94A3B8', fontSize: '0.78rem' }}>{t.auth.login.noAccount + ' '}</span>
            <Link to={lp('register')} style={{ color: '#D42B2B', fontSize: '0.78rem', textDecoration: 'none', letterSpacing: '0.05em' }}>{t.auth.login.register}</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
